/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled'
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { Alert, Select, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import Text from '../../Text'
import Button from '../../Button'
import ProgressHeader from '../components/ProgressHeader'
import { useOnboardingWizard } from '../context/OnboardingWizardContext'
import colors from '@sportninja/common/constants/appColors'
import { TextInput } from '../../TextInput'
import { SelectInput } from '../../SelectInput'
import RangePicker from '../../RangePicker'
import DatePicker from '../../DatePicker'
import dayjs from 'dayjs'
import { SportEntry } from '../context/OnboardingWizardContext'
import req from '@sportninja/common/api/request'
import useWindowSize from 'src/hooks/useWindowSize'
import { trackOnboardingFirstCompetition } from 'src/utils/analytics'
import { paragraphSmall, weightSemiBold } from 'src/components/css'

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const ContentContainer = styled.div`
  flex: 1;
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 16px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InputRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`

const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
`

const NavigationButtons = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`

const SkipLink = styled.button`
  background: none;
  border: none;
  color: ${colors.PRIMARY_300};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

const DatePickersRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

/**
 * First Competition step component - Step 3 of the onboarding wizard
 * Allows users to create their first competition.
 */
const FirstCompetition: React.FC = () => {
  const {
    previousStep,
    completeWizard,
    skipWizard,
    sportsData,
    sports,
    competitionFormData,
    setCompetitionFormData,
  } = useOnboardingWizard()

  // Form State - initialize from saved data if available
  const [competitionName, setCompetitionName] = useState(
    competitionFormData?.name || ''
  )
  const [selectedLeagueEntry, setSelectedLeagueEntry] = useState<
    SportEntry | undefined
  >()
  const [selectedSportOverride, setSelectedSportOverride] = useState<
    string | null
  >(competitionFormData?.selectedSport || null)
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    competitionFormData?.startDate ? dayjs(competitionFormData.startDate) : null
  )
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(
    competitionFormData?.endDate ? dayjs(competitionFormData.endDate) : null
  )
  const [fieldErrors, setFieldErrors] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Mobile detection
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 1024, [width])

  // Track previous sportsData to detect changes from SportsSelection step
  const prevSportsDataRef = useRef<string | null>(null)
  // Use refs to access current values without adding them to effect dependencies
  const selectedLeagueEntryRef = useRef(selectedLeagueEntry)
  const selectedSportOverrideRef = useRef(selectedSportOverride)

  // Keep refs in sync with state
  useEffect(() => {
    selectedLeagueEntryRef.current = selectedLeagueEntry
  }, [selectedLeagueEntry])

  useEffect(() => {
    selectedSportOverrideRef.current = selectedSportOverride
  }, [selectedSportOverride])

  useEffect(() => {
    if (!sportsData || sportsData.length === 0) {
      return
    }

    // Create a signature of sportsData to detect changes
    const sportsDataSignature = JSON.stringify(
      sportsData.map((s) => ({
        id: s.id,
        sport_id: s.sport_id,
        generic_sport_id: s.generic_sport_id,
        is_generic: s.is_generic,
      }))
    )

    const sportsDataChanged = prevSportsDataRef.current !== sportsDataSignature
    prevSportsDataRef.current = sportsDataSignature

    // If sportsData changed (user went back and modified sports), clear the sport override
    if (sportsDataChanged && selectedSportOverrideRef.current) {
      setSelectedSportOverride(null)
    }

    // Find the matching league entry
    let newLeagueEntry: SportEntry | undefined

    if (competitionFormData?.selectedLeague) {
      newLeagueEntry = sportsData.find(
        (league) => league.leagueName === competitionFormData.selectedLeague
      )
    }

    // Default to first entry if not found
    if (!newLeagueEntry) {
      newLeagueEntry = sportsData[0]
    }

    // Update if different (comparing by relevant properties)
    const currentEntry = selectedLeagueEntryRef.current
    const currentSig = currentEntry
      ? `${currentEntry.id}-${currentEntry.sport_id}-${currentEntry.generic_sport_id}`
      : null
    const newSig = newLeagueEntry
      ? `${newLeagueEntry.id}-${newLeagueEntry.sport_id}-${newLeagueEntry.generic_sport_id}`
      : null

    if (currentSig !== newSig) {
      setSelectedLeagueEntry(newLeagueEntry)
    }
  }, [sportsData, competitionFormData?.selectedLeague])

  // Save form data to context whenever any field changes
  useEffect(() => {
    const formData = {
      name: competitionName,
      selectedLeague: selectedLeagueEntry?.leagueName || null,
      selectedSport: selectedSportOverride,
      startDate: startDate?.toISOString() || null,
      endDate: endDate?.toISOString() || null,
    }
    setCompetitionFormData(formData)
  }, [
    competitionName,
    selectedLeagueEntry,
    selectedSportOverride,
    startDate,
    endDate,
    setCompetitionFormData,
  ])

  const leagueOptions = useMemo(() => {
    return (
      sportsData?.map((league) => ({
        label: league.leagueName,
        value: league.leagueName,
      })) || []
    )
  }, [sportsData])

  // Build grouped sport options (Full Stats vs Basic Stats)
  // Values use composite IDs: "full:{sport_id}" or "basic:{generic_sport_id}"
  const { groupedSportOptions, flatSportOptions } = useMemo(() => {
    if (!sports || sports.length === 0) {
      return { groupedSportOptions: [], flatSportOptions: [] }
    }

    const fullStatsOptions = sports
      .filter((sport) => !sport.is_generic)
      .map((sport) => ({
        label: sport.name_full,
        // Use composite ID for reliable matching
        value: `full:${sport.sport_id}`,
        is_generic: false,
        sport_id: sport.sport_id,
        generic_sport_id: sport.generic_sport_id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    const basicStatsOptions = sports
      .filter((sport) => sport.is_generic)
      .map((sport) => ({
        label: `${sport.name_full} (Basic)`,
        // Use composite ID for reliable matching
        value: `basic:${sport.generic_sport_id}`,
        is_generic: true,
        sport_id: sport.sport_id,
        generic_sport_id: sport.generic_sport_id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    const grouped = [
      {
        label: (
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <span
              css={css`
                ${paragraphSmall}
                ${weightSemiBold}
                color: ${colors.PRIMARY};
                text-transform: uppercase;
              `}
            >
              Full Stats
            </span>
            <Tooltip title="What's the difference between Full Stats vs Basic Stats?">
              <QuestionCircleOutlined
                style={{
                  fontSize: 16,
                  color: colors.PRIMARY,
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(
                    'https://support.sportninja.com/scoring-2-versions-basic-vs-full-stats',
                    '_blank'
                  )
                }}
              />
            </Tooltip>
          </div>
        ),
        options: fullStatsOptions,
      },
      {
        label: (
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <span
              css={css`
                ${paragraphSmall}
                ${weightSemiBold}
                color: ${colors.PRIMARY};
                text-transform: uppercase;
              `}
            >
              Basic Stats
            </span>
            <Tooltip title="What's the difference between Full Stats vs Basic Stats?">
              <QuestionCircleOutlined
                style={{
                  fontSize: 16,
                  color: colors.PRIMARY,
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(
                    'https://support.sportninja.com/scoring-2-versions-basic-vs-full-stats',
                    '_blank'
                  )
                }}
              />
            </Tooltip>
          </div>
        ),
        options: basicStatsOptions,
      },
    ]

    return {
      groupedSportOptions: grouped,
      flatSportOptions: [...fullStatsOptions, ...basicStatsOptions],
    }
  }, [sports])

  // Filter function for grouped options (excludes group headers from filter)
  const filterOptionGroup = useCallback(
    (searchText: string, optionOrGroup: any) => {
      const isGroup = Array.isArray(optionOrGroup.options)
      if (isGroup) {
        return false
      }
      return optionOrGroup.label
        ?.toString()
        .toLowerCase()
        .includes(searchText.toLowerCase())
    },
    []
  )

  // State for Tab-to-select on sport dropdown
  const [sportDropdownOpen, setSportDropdownOpen] = useState(false)
  const sportSearchRef = useRef('')

  // Tab-to-select handler for sport dropdown
  const handleSportKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && sportDropdownOpen && sportSearchRef.current) {
      const matching = flatSportOptions.find((opt) =>
        opt.label.toLowerCase().includes(sportSearchRef.current.toLowerCase())
      )
      if (matching) {
        e.preventDefault()
        setSelectedSportOverride(matching.value)
        setSportDropdownOpen(false)
      }
    }
  }

  // Returns the composite ID value for the selected sport (e.g., "full:123" or "basic:456")
  const selectedSportValue = useMemo(() => {
    // If user has overridden the sport, use that (already in composite ID format)
    if (selectedSportOverride) {
      return selectedSportOverride
    }

    // Otherwise, derive from the selected league entry
    if (!selectedLeagueEntry || !sports || sports.length === 0) {
      return ''
    }

    const league = selectedLeagueEntry

    if (league.is_generic) {
      // For generic/basic stats sports, use generic_sport_id
      if (league.generic_sport_id) {
        return `basic:${league.generic_sport_id}`
      }
    } else {
      // For full stats sports, use sport_id
      if (league.sport_id) {
        return `full:${league.sport_id}`
      }
    }

    return ''
  }, [selectedLeagueEntry, sports, selectedSportOverride])

  // Helper to find sport by composite ID value
  const findSportByValue = useCallback(
    (value: string) => {
      if (!value || !sports) {
        return null
      }

      if (value.startsWith('full:')) {
        const sportId = value.replace('full:', '')
        return sports.find((s) => !s.is_generic && s.sport_id === sportId)
      } else if (value.startsWith('basic:')) {
        const genericSportId = value.replace('basic:', '')
        return (
          sports.find(
            (s) => s.is_generic && s.generic_sport_id === genericSportId
          ) || null
        )
      }

      return null
    },
    [sports]
  )

  const handleDateRangeChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  ) => {
    if (dates) {
      const [start, end] = dates
      setStartDate(start)
      setEndDate(end)
    } else {
      setStartDate(null)
      setEndDate(null)
    }
  }

  // Separate handlers for individual DatePickers
  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    setStartDate(date)
  }

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    setEndDate(date)
  }

  const handleCreate = async () => {
    // Clear previous errors
    setError(null)
    // Basic validation
    const errors: any = {}
    if (!competitionName) {
      errors.competitionName = 'Competition name is required'
    }
    if (!selectedLeagueEntry) {
      errors.selectedLeague = 'Location is required'
    }
    if (!startDate || !endDate) {
      errors.dateRange = ['Date range is required']
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }

    // Find the sport by ID (using composite value like "full:123" or "basic:456")
    const selectedSport = findSportByValue(selectedSportValue)

    const apiPayload = {
      name: competitionName,
      starts_at: startDate?.format('YYYY-MM-DD'),
      ends_at: endDate?.format('YYYY-MM-DD'),
      league_name: selectedLeagueEntry?.leagueName,
      // Include sport override data if user selected different sport
      sport_id: selectedSport?.sport_id || selectedLeagueEntry?.sport_id,
      generic_sport_id:
        selectedSport?.generic_sport_id ||
        selectedLeagueEntry?.generic_sport_id,
      leagues: sportsData
        ? sportsData.map((league) => ({
            name: league.leagueName,
            sport_id: selectedSportOverride
              ? selectedSport?.sport_id
              : league.sport_id,
            generic_sport_id: selectedSportOverride
              ? selectedSport?.generic_sport_id
              : league.generic_sport_id,
            is_generic: selectedSportOverride
              ? selectedSport?.is_generic
              : league.is_generic,
          }))
        : [],
    }

    // Track the first competition creation
    trackOnboardingFirstCompetition({
      competition_name: competitionName,
      sport: selectedSport?.name_full || '',
      subleague: selectedLeagueEntry?.leagueName || '',
    })

    setLoading(true)
    try {
      const body = JSON.stringify(apiPayload)
      const response = await req('/wizard/league', { method: 'POST', body })

      // Extract schedule ID from response
      const scheduleId = response?.data?.schedule?.id
      if (scheduleId) {
        completeWizard(scheduleId)
      } else {
        completeWizard()
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to create competition. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const canProceed =
    competitionName && selectedLeagueEntry && startDate && endDate

  return (
    <StepContainer>
      <style>
        {`
          .sport-select-dropdown .ant-select-item-option-content {
            font-size: 14px !important;
            color: ${colors.WHITE} !important;
          }
          .sport-select-dropdown .ant-select-item-group {
            font-size: 12px;
            font-weight: 600;
          }
          .sport-select-dropdown {
            max-height: 300px !important;
          }
          .sport-select-dropdown .rc-virtual-list-holder {
            max-height: 280px !important;
          }
        `}
      </style>
      <ProgressHeader currentStep={3} />

      <ContentContainer>
        <TitleSection>
          <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
            Set Up Your First Competition!
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            Fill in the details below to create your competition.
          </Text>
        </TitleSection>

        <FormContainer>
          <InputWrapper>
            <TextInput
              name='competitionName'
              value={competitionName}
              setValue={setCompetitionName}
              fieldErrors={fieldErrors}
              customError={{}}
              label='Competition Name *'
              placeholder="Summer Showdown '25"
              maxLength={100}
            />
          </InputWrapper>

          <InputRow>
            <InputWrapper>
              <SelectInput
                label='Where should this live?'
                name='selectedLeague'
                value={selectedLeagueEntry?.leagueName || ''}
                onChange={(leagueName) => {
                  const entry = sportsData?.find(
                    (l) => l.leagueName === leagueName
                  )
                  setSelectedLeagueEntry(entry)
                  // Clear sport override when changing league - use the league's sport
                  setSelectedSportOverride(null)
                }}
                options={leagueOptions}
                placeholder='Select a location'
                fieldErrors={fieldErrors}
                customError={{}}
                disabled={!sportsData || sportsData.length === 0}
              />
            </InputWrapper>
            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                @media (max-width: 768px) {
                  width: 100%;
                }
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.WHITE}
                style={'margin-bottom: 8px;'}
              >
                Sport
              </Text>
              <Select
                placeholder='Select a sport'
                value={selectedSportValue || undefined}
                onChange={(sportValue) => {
                  setSelectedSportOverride(sportValue)
                }}
                options={groupedSportOptions}
                disabled={!sports || sports.length === 0}
                showSearch
                filterOption={filterOptionGroup}
                open={sportDropdownOpen}
                onDropdownVisibleChange={setSportDropdownOpen}
                onSearch={(val) => {
                  sportSearchRef.current = val
                }}
                onKeyDown={handleSportKeyDown}
                css={css`
                  font-size: 14px !important;
                  color: ${colors.WHITE};
                  width: 100%;
                  background: ${colors.SECONDARY_200} !important;
                  border-radius: 8px !important;
                  height: 40px !important;
                  ${!sports || sports.length === 0
                    ? 'opacity: 0.5 !important;'
                    : ''}
                `}
                popupClassName='sport-select-dropdown'
              />
            </div>
          </InputRow>

          <InputWrapper>
            {isMobile ? (
              <DatePickersRow>
                <DatePicker
                  label='Start Date *'
                  value={startDate}
                  onChange={handleStartDateChange}
                />
                <DatePicker
                  label='End Date *'
                  value={endDate}
                  onChange={handleEndDateChange}
                  minDate={startDate}
                />
              </DatePickersRow>
            ) : (
              <RangePicker
                label='Competition Dates *'
                name='dateRange'
                starts_at={startDate}
                ends_at={endDate}
                onChange={handleDateRangeChange}
                fieldErrors={fieldErrors.dateRange || []}
                customError={null}
              />
            )}
            {isMobile && fieldErrors.dateRange?.length > 0 && (
              <div
                css={css`
                  margin-top: 8px;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.ERROR_300}
                >
                  {fieldErrors.dateRange[0]}
                </Text>
              </div>
            )}
          </InputWrapper>
        </FormContainer>

        {error && (
          <Alert
            message={error}
            type='error'
            showIcon
            style={{ marginTop: '8px' }}
          />
        )}

        <ActionsContainer>
          <NavigationButtons>
            <Button
              text='BACK'
              onClick={previousStep}
              variant='secondary'
              uppercase
              style={css`
                flex: 1;
              `}
            />
            <Button
              text={loading ? 'CREATING...' : 'CREATE'}
              onClick={handleCreate}
              variant='primary'
              uppercase
              disabled={!canProceed || loading}
              style={css`
                flex: 1;
              `}
            />
          </NavigationButtons>

          <SkipLink onClick={skipWizard}>Skip Setup Wizard</SkipLink>
        </ActionsContainer>
      </ContentContainer>
    </StepContainer>
  )
}

export default FirstCompetition
